// §26.2 / §26.3 / §26.6 — the client half of the social layer.
//
// One service, three jobs: talk to `/api/social/*`, hold the doorbell open, and run the heartbeat.
// It lives in `kernel/` because it owns a network connection and a timer, and because §6.5 keeps
// `apps/**` away from both — the Connections app receives a capability (D-059), never this.
//
// **The doorbell is not the data** (D-217). A frame says what changed; this service answers by
// re-`GET`ting and telling its own listeners. So the app never parses a frame, and there is exactly
// one code path that produces a roster — the same one a cold load uses.
//
// **§26.8 F10 is built in, not bolted on.** If the stream will not connect, or dies and cannot come
// back, presence falls back to a 60-second poll and everything else keeps working with added
// latency. The fallback is the *same* refresh the doorbell triggers, so there is no second
// behaviour to get wrong.

import { SOCIAL_EVENTS } from '@hangar/shared';
import type {
  Connection,
  DailyScore,
  DailyScoreEntry,
  HeadToHead,
  Presence,
  PresenceState,
  SocialEvent,
} from '@hangar/shared';

export interface SocialServiceOptions {
  /** Whether there is a signed-in user. A guest never touches any of this (§26.9). */
  signedIn: () => boolean;
  /** §26.2 (D-344) — what the first heartbeat announces. Read at `start()`, not captured. */
  initialStatus?: () => PresenceState;
  /** Injected in tests; the real one in production. */
  fetch?: typeof globalThis.fetch;
  /** Injected in tests; `EventSource` in production. Absent ⇒ straight to the F10 poll. */
  createEventSource?: (url: string) => EventSourceLike;
  setTimer?: (fn: () => void, ms: number) => number;
  clearTimer?: (handle: number) => void;
  onError?: (error: unknown) => void;
}

/**
 * The slice of `EventSource` this uses. Structural, so a fake needs two members and no DOM.
 *
 * Failure arrives through `addEventListener('error', …)` rather than through `onerror`, which is
 * the same event by a second name and the one that does **not** typecheck against a hand-written
 * seam: `lib.dom` declares `onerror` with `this: EventSource`, and a `this` parameter is
 * contravariant, so no widening of the seam's signature admits the real class without a cast.
 * `addEventListener` has no such problem and is the form that supports more than one listener,
 * which is what a seam should offer anyway.
 */
export interface EventSourceLike {
  addEventListener(type: string, listener: (event: { data?: string }) => void): void;
  close(): void;
}

export interface SocialService {
  connections(): Promise<Connection[]>;
  presence(): Promise<Presence[]>;
  invite(): Promise<{ token: string; expiresAt: number; link: string }>;
  accept(token: string): Promise<{ peer: string; displayName: string; already: boolean }>;
  remove(peer: string): Promise<void>;
  block(peer: string): Promise<void>;
  heartbeat(status: PresenceState, gameId: string | null): Promise<void>;
  /**
   * §26.2 (D-344) — change the status and tell everyone **now**.
   *
   * Not `heartbeat(status, null)`: that would clear the game as a side effect, so picking *Busy*
   * mid-Conquest would tell every connection you had stopped playing. The point of a separate verb
   * is that the two halves of a presence row have different owners — the status is the user's and
   * the game is the shell's — and neither should be able to blank the other.
   */
  setStatus(status: PresenceState): Promise<void>;
  /**
   * §30.4 (D-371) — record a finished daily deal.
   *
   * Answers whether the stored row changed, so a caller can tell "that was your best today" from
   * "you have done better". The caller's ref is the session's; there is no parameter for it, here
   * or on the wire.
   */
  postDailyScore(entry: DailyScoreEntry): Promise<boolean>;
  /** §30.4 — one day's board for one game: the caller and their accepted connections, ranked. */
  dailyScores(gameId: string, day?: string): Promise<DailyScore[]>;
  /** §30.5 — lifetime W-L-D against one connection, per game. */
  headToHead(peer: string): Promise<HeadToHead[]>;
  /** Anything social changed — a doorbell frame, or the F10 poll firing. */
  subscribe(listener: (event: SocialEvent) => void): () => void;
  /** Open the stream and start the heartbeat. Idempotent; safe to call on every sign-in. */
  start(): void;
  stop(): void;
}

/** §26.2 — the heartbeat cadence. */
export const HEARTBEAT_MS = 30_000;
/** §26.8 F10 — the poll that stands in for a stream that will not connect. */
export const FALLBACK_POLL_MS = 60_000;

const BASE = '/api/social';

export function createSocialService(options: SocialServiceOptions): SocialService {
  const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);
  const setTimer =
    options.setTimer ?? ((fn, ms) => globalThis.setInterval(fn, ms) as unknown as number);
  const clearTimer = options.clearTimer ?? ((handle) => globalThis.clearInterval(handle));
  const report = options.onError ?? ((error: unknown) => console.warn('social', error));

  const listeners = new Set<(event: SocialEvent) => void>();
  let source: EventSourceLike | null = null;
  let heartbeatTimer: number | null = null;
  let pollTimer: number | null = null;
  let lastStatus: PresenceState = 'online';
  let lastGame: string | null = null;

  const ring = (event: SocialEvent): void => {
    for (const listener of [...listeners]) {
      try {
        listener(event);
      } catch (error) {
        report(error);
      }
    }
  };

  const json = async <T>(path: string, init?: RequestInit): Promise<T> => {
    const response = await doFetch(`${BASE}${path}`, { credentials: 'same-origin', ...init });
    if (!response.ok) throw new Error(`${BASE}${path} answered ${response.status}`);
    // 204s carry nothing; asking for JSON would throw on a route that answered correctly.
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  };

  /**
   * F10's fallback, started only when the stream is unavailable or has failed.
   *
   * It rings the same listeners the doorbell would, so nothing downstream knows which one it got —
   * which is what stops "works over SSE, broken on the poll" from being a state that can exist.
   */
  const startPolling = (): void => {
    if (pollTimer !== null) return;
    pollTimer = setTimer(() => ring({ kind: 'presence', from: '' }), FALLBACK_POLL_MS);
  };

  const stopPolling = (): void => {
    if (pollTimer === null) return;
    clearTimer(pollTimer);
    pollTimer = null;
  };

  return {
    connections: async () =>
      (await json<{ connections: Connection[] }>('/connections')).connections ?? [],
    presence: async () => (await json<{ presence: Presence[] }>('/presence')).presence ?? [],
    invite: () =>
      json<{ token: string; expiresAt: number; link: string }>('/invites', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: '{}',
      }),
    accept: (token) =>
      json<{ connection: { peer: string; displayName: string }; already: boolean }>(
        '/connections/accept',
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ token }),
        },
      ).then((body) => ({
        peer: body.connection.peer,
        displayName: body.connection.displayName,
        already: body.already,
      })),
    remove: async (peer) => {
      await json<void>(`/connections/${encodeURIComponent(peer)}`, { method: 'DELETE' });
    },
    block: async (peer) => {
      await json<void>(`/connections/${encodeURIComponent(peer)}/block`, { method: 'POST' });
    },

    async postDailyScore(entry) {
      const body = await json<{ stored: boolean }>('/daily-score', {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(entry),
      });
      return body.stored === true;
    },

    async dailyScores(gameId, day) {
      const query = new URLSearchParams({ gameId, ...(day === undefined ? {} : { day }) });
      return (await json<{ scores: DailyScore[] }>(`/daily-scores?${query.toString()}`)).scores ?? [];
    },

    async headToHead(peer) {
      const query = new URLSearchParams({ peer });
      return (await json<{ records: HeadToHead[] }>(`/head-to-head?${query.toString()}`)).records ?? [];
    },

    async setStatus(status) {
      await this.heartbeat(status, lastGame);
    },

    async heartbeat(status, gameId) {
      lastStatus = status;
      lastGame = gameId;
      await json<void>('/presence', {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ status, gameId }),
      });
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    start() {
      if (!options.signedIn()) return;
      // Read here rather than at construction: the kernel is built before the settings have been
      // hydrated, so a value captured then would always be the default.
      lastStatus = options.initialStatus?.() ?? lastStatus;
      if (heartbeatTimer === null) {
        /**
         * **One immediately, then every 30 s** (D-318).
         *
         * `setInterval` alone fires first at +30 s, which meant a signed-in person had no presence
         * row for the first half minute and read as `offline` to every connection — the state
         * `derivePresence` gives somebody who has never checked in. Nothing looked broken: the dot
         * simply took a while to come on, which is indistinguishable from "they have not opened it
         * yet". The two-context e2e is what made it visible, because it asks two people who just
         * signed in whether they can see each other.
         */
        void this.heartbeat(lastStatus, lastGame).catch(report);
        heartbeatTimer = setTimer(() => {
          void this.heartbeat(lastStatus, lastGame).catch(report);
        }, HEARTBEAT_MS);
      }
      if (source !== null) return;

      const make = options.createEventSource;
      if (make === undefined) {
        // No `EventSource` at all — a test environment, or a browser that has none. F10 says this
        // degrades rather than fails.
        startPolling();
        return;
      }
      try {
        const stream = make(`${BASE}/events`);
        source = stream;
        /**
         * **Every** frame kind, from the one list that defines them (D-355).
         *
         * `EventSource` delivers a named event only to a listener for that name, so a kind missing
         * from this loop is a kind that silently never arrives — and the symptom is not an error,
         * it is a feature that works on the F10 fallback poll and not over a healthy stream. `27`
         * added `reminder` to `SOCIAL_EVENTS` and not to the literal that used to be here, so a
         * shared list never refreshed on the other person's screen unless their stream was down.
         * Reading the constant is what stops M30's `move` being the next one.
         */
        for (const kind of SOCIAL_EVENTS) {
          stream.addEventListener(kind, (event) => {
            let parsed: SocialEvent = { kind, from: '' };
            try {
              if (typeof event.data === 'string') parsed = JSON.parse(event.data) as SocialEvent;
            } catch {
              // A frame we cannot read is still a frame: something changed, so ring anyway. The
              // listener re-`GET`s, which is the only thing it was ever going to do.
            }
            // A live stream means the poll is redundant — stopping it here rather than at `open`
            // covers the case where the connection succeeds after a failure.
            stopPolling();
            ring(parsed);
          });
        }
        stream.addEventListener('error', () => {
          // `EventSource` reconnects on its own; the poll covers the gap and is stopped again by
          // the first frame that arrives.
          startPolling();
        });
      } catch (error) {
        report(error);
        startPolling();
      }
    },

    stop() {
      source?.close();
      source = null;
      stopPolling();
      if (heartbeatTimer !== null) {
        clearTimer(heartbeatTimer);
        heartbeatTimer = null;
      }
    },
  };
}
